import 'dayjs/locale/pt-br';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ptBR } from '@mui/x-date-pickers/locales';

import { LoginPage, PasswordRecoveryPage, PasswordResetPage, DashboardPage, ListCreditPoliciesPage, CreateCreditPolicyPage, BatchFilePage, BypassCustomersPage, CustomersPage } from '../pages';
import { acSetDarkMode } from '../store/actions';
import { ROUTES } from '../store/constants';
import { LightTheme, DarkTheme } from '../theme';
import ProtectedRoute from './ProtectedRoute';

/**
 * The main component of the application.
 * This component is responsible for rendering the entire application and managing the theme and routes.
 *
 * @author Maria Eduarda Franke
 * @returns {JSX.Element} The rendered application.
 */
function App() {

    const dispatch = useDispatch()
    const darkMode = useSelector(state => state.auth.darkMode)

    useEffect(() => {
        dispatch(acSetDarkMode(false))
        // eslint-disable-next-line
    }, [])

    return (
        <ThemeProvider theme={darkMode ? DarkTheme : LightTheme}>
            <CssBaseline />
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale='pt-br'
                localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <BrowserRouter>
                    <Routes>
                        {/* UNPROTECTED ROUTES */}
                        <Route path={ROUTES.LOGIN} Component={LoginPage} />
                        <Route path={ROUTES.PASSWORD_RECOVERY} Component={PasswordRecoveryPage} />
                        <Route path={ROUTES.PASSWORD_RESET} Component={PasswordResetPage} />

                        {/* PROTECTED ROUTES */}
                        <Route
                            path={ROUTES.DASHBOARD}
                            element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
                        />
                        <Route
                            path={ROUTES.CREDIT_POLICIES_LIST}
                            element={<ProtectedRoute><ListCreditPoliciesPage /></ProtectedRoute>}
                        />
                        <Route
                            path={ROUTES.CREDIT_POLICIES_CREATE}
                            element={<ProtectedRoute><CreateCreditPolicyPage /></ProtectedRoute>}
                        />
                        <Route
                            path={ROUTES.BYPASS_LIST}
                            element={<ProtectedRoute><BypassCustomersPage /></ProtectedRoute>}
                        />
                        <Route
                            path={ROUTES.BATCH_FILE}
                            element={<ProtectedRoute><BatchFilePage /></ProtectedRoute>}
                        />
                        <Route
                            path={ROUTES.CUSTOMERS}
                            element={<ProtectedRoute><CustomersPage /></ProtectedRoute>}
                        />

                        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
                    </Routes>
                </BrowserRouter>
            </LocalizationProvider>
        </ThemeProvider>
    )
}

export default App
